import {AnimatePresence, m, Variants} from 'framer-motion'
import _ from 'lodash'
import React, {useEffect} from 'react'

import {MODAL_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'

type OverlayProps = {
  close?: (e?: any) => void
  visible?: boolean
  backgroundColor?: string
}

const overlayVariants: Variants = {
  hidden: {
    opacity: 0,
    transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1], delay: MODAL_TRANSITION / 2},
  },
  visible: {opacity: 1, transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1]}},
}

const Overlay: React.FC<React.PropsWithChildren<OverlayProps>> = ({
  close,
  visible = true,
  backgroundColor = theme.palette.overlay.main,
}) => {
  const className = useUniqueClassName('motion-overlay')

  // Disable scrolling of the page, padding it by the hidden scrollbar's width so it doesn't shift.
  // Closing always unlocks, so overlays closing in any order can't leave the page locked.
  useEffect(() => {
    if (!visible) {
      return
    }

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [visible])

  return (
    <AnimatePresence propagate>
      {visible && (
        <m.div
          key="overlay"
          className={className}
          onClick={(e) => {
            e.preventDefault()
            close && close(e)
          }}
          role="button"
          tabIndex={0}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          // Any onUpdate keeps framer-motion off native browser animations. Those commit the final
          // opacity a frame late, so the overlay flashed at the end of every fade whenever no other
          // framer-motion animation was running (e.g. behind the help center drawer)
          onUpdate={_.noop}
        />
      )}

      <style jsx global>{`
        .${className} {
          width: 100%;
          height: 100%;
          position: fixed;
          top: 0;
          right: 0px;
          bottom: 0px;
          left: 0px;
          z-index: 2;
          min-height: 100vh;
          min-height: 100svh;
          background-color: ${backgroundColor};
          cursor: default;
        }
      `}</style>
    </AnimatePresence>
  )
}

export default Overlay
